import { LitElement, PropertyValues, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import * as TagNames from './graphs.tagnames.ts';
import {
   enumAttributeConverter,
   Sizes,
   SizesValuesMap,
   Variant,
   VariantValuesMap,
} from '../enum-types.ts';
import { AtlCircularProgressProps } from './graphs.props.ts';

import CircularProgressStyles from './circular-progress.scss?inline';

@customElement(TagNames.CircularProgress)
export class AtlCircularProgress
   extends LitElement
   implements AtlCircularProgressProps
{
   declare size?: Sizes;
   declare stroke?: number;
   declare labelSize?: Sizes;
   declare asNumber?: boolean;
   declare customLabel?: string;
   declare progress?: number;
   declare variant?: Variant;

   declare _strokeDashoffset?: number;

   private _animationRingInterval?: ReturnType<typeof setInterval>;

   static properties = {
      size: { type: String, converter: enumAttributeConverter(Sizes) },
      stroke: { type: Number },
      labelSize: {
         attribute: 'label-size',
         type: String,
         converter: enumAttributeConverter(Sizes),
      },
      asNumber: { attribute: 'as-number', type: Boolean },
      customLabel: { attribute: 'custom-label', type: String },
      progress: { type: Number },
      variant: { type: String, converter: enumAttributeConverter(Variant) },

      _strokeDashoffset: { type: Number, state: true },
   };

   static styles = [CircularProgressStyles];

   @query('circle.progress')
   private progressCircle?: SVGElement;

   get _size() {
      return this.size || Sizes.Medium;
   }

   get _radius() {
      switch (this._size) {
         case Sizes.XSmall:
            return 20;
         case Sizes.Smaller:
            return 30;
         case Sizes.Small:
            return 35;
         case Sizes.Medium:
            return 40;
         case Sizes.Large:
            return 45;
         case Sizes.Larger:
            return 50;
         case Sizes.XLarge:
            return 60;
         case Sizes.XXLarge:
            return 70;
      }
   }

   get _stroke() {
      if (!this.stroke) return this.size === Sizes.XSmall ? 2 : 4;
      return Math.min(Math.max(2, this.stroke), 10);
   }

   get _normalizedRadius() {
      return this._radius - this._stroke * 2;
   }

   get _circumference() {
      return this._normalizedRadius * 2 * Math.PI;
   }

   get _labelSize() {
      return this.labelSize || this._size;
   }

   get _label() {
      if (!this.customLabel) {
         return `${this.progress || 0}${this.asNumber ? '' : '%'}`;
      }
      return this.customLabel.substring(0, this.size == Sizes.XSmall ? 4 : 7);
   }

   disconnectedCallback(): void {
      clearInterval(this._animationRingInterval);
      super.disconnectedCallback();
   }

   protected updated(changedProperties: PropertyValues): void {
      if (changedProperties.has('progress')) {
         this._animateRing();
      }
   }

   protected override firstUpdated(changedProperties: PropertyValues): void {
      super.firstUpdated(changedProperties);

      this._animateRing();
   }

   private _animateRing = () => {
      if (!this.progressCircle) return;

      this._strokeDashoffset = this._circumference;

      const targetProgress = this.progress || 0;

      let progress = 0;
      const progressStep = 10;
      const progressIncremental = targetProgress / progressStep;
      clearInterval(this._animationRingInterval);

      this._animationRingInterval = setInterval(() => {
         progress += progressIncremental;
         this._strokeDashoffset =
            this._circumference - (progress / 100) * this._circumference;
         if (progress >= targetProgress) {
            clearInterval(this._animationRingInterval);
         }
      }, 50);
   };

   render() {
      return html`
         <svg
            height="${this._radius * 2}"
            width="${this._radius * 2}"
            class="circular-progress ${VariantValuesMap[
               this.variant || Variant.Primary
            ]}"
         >
            <circle
               class="bg"
               stroke-width="${this._stroke}"
               fill="transparent"
               r="${this._normalizedRadius}"
               cx="${this._radius}"
               cy="${this._radius}"
            />
            <circle
               class="progress"
               stroke-dasharray="${this._circumference} ${this._circumference}"
               style="stroke-dashoffset:${this._strokeDashoffset || 0}"
               stroke-width="${this._stroke}"
               fill="transparent"
               r="${this._normalizedRadius}"
               cx="${this._radius}"
               cy="${this._radius}"
            />
            <text
               x="${this._radius}"
               y="${this._radius}"
               alignment-baseline="middle"
               text-anchor="middle"
               class="label ${SizesValuesMap[this._labelSize]}"
            >
               ${this._label}
            </text>
         </svg>
      `;
   }
}
